import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { apiErrorMessage } from '../../services/api'
import { Icon } from '../../components/ui/icons'
import { ErrorText, Spinner } from '../../components/ui'
import { CLINIC, WHATSAPP_URL } from '../../config/clinic'
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABELS, formatDate, formatTime, MODALITY_LABELS } from '../../utils/format'
import { BookingShell, ProfessionalAvatar } from './BookingShell'
import { cancelByToken, confirmByToken, fetchSelfService } from './booking.api'

/**
 * Autoservicio de la cita por enlace (DT-57): `/cita/<token>`. Es la página
 * a la que llevan los correos. Muestra la hora y permite confirmarla o
 * anularla sin login ni volver a escribir el RUT — lo que RedSalud no hace.
 *
 * `?accion=confirmar` confirma al abrir (un toque desde el correo). Anular
 * exige un clic más a propósito: un enlace abierto por error no debe liberar
 * un cupo.
 */
export function CitaPage() {
  const { token = '' } = useParams<{ token: string }>()
  const [params] = useSearchParams()
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [askCancel, setAskCancel] = useState(params.get('accion') === 'anular')
  const [autoConfirmed, setAutoConfirmed] = useState(false)

  const view = useQuery({
    queryKey: ['cita', token],
    queryFn: () => fetchSelfService(token),
    enabled: token.length > 0,
    retry: false,
  })

  const confirm = useMutation({
    mutationFn: () => confirmByToken(token),
    onSuccess: (data) => {
      queryClient.setQueryData(['cita', token], data)
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })
  const cancel = useMutation({
    mutationFn: () => cancelByToken(token),
    onSuccess: (data) => {
      queryClient.setQueryData(['cita', token], data)
      setAskCancel(false)
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  // Confirmación en un toque desde el correo.
  useEffect(() => {
    if (autoConfirmed || !view.data || params.get('accion') !== 'confirmar') return
    setAutoConfirmed(true)
    if (view.data.canConfirm) confirm.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.data])

  if (view.isLoading) {
    return (
      <BookingShell step={4}>
        <Spinner />
      </BookingShell>
    )
  }

  if (view.isError || !view.data) {
    return (
      <BookingShell step={4}>
        <div className="space-y-4 rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-display text-xl font-bold text-red-700">Este enlace no corresponde a ninguna cita</p>
          <p className="text-sm text-red-700">Puede estar incompleto o haber vencido. Si necesitas ayuda, escríbenos.</p>
          <div className="flex flex-wrap justify-center gap-3 text-sm">
            <Link to="/agendar" className="font-medium text-brand-700 hover:underline">Reservar una hora</Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-700 hover:underline">WhatsApp</a>
          </div>
        </div>
      </BookingShell>
    )
  }

  const a = view.data
  const active = ['REQUESTED', 'SCHEDULED', 'CONFIRMED'].includes(a.status)
  const past = new Date(a.endsAt) < new Date()

  return (
    <BookingShell step={4}>
      <div className="space-y-5">
        <div>
          <p className="text-sm text-steel-600">Hola, {a.patientFirstName}</p>
          <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">Tu cita en Cima Sport</h1>
        </div>

        <div className="rounded-2xl bg-brand-900 p-4 text-white">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">
              {a.status === 'CANCELLED' ? 'Cita anulada' : past ? 'Cita pasada' : 'Tu hora'}
            </p>
            <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', APPOINTMENT_STATUS_COLORS[a.status])}>
              {APPOINTMENT_STATUS_LABELS[a.status]}
            </span>
          </div>
          <p className={clsx('mt-1 font-display text-xl font-bold', a.status === 'CANCELLED' && 'line-through opacity-70')}>
            {formatDate(a.startsAt)} · {formatTime(a.startsAt)} h
          </p>
          <p className="mt-1 text-sm text-brand-100">
            {a.service.name} · {a.service.durationMin} min · {MODALITY_LABELS[a.modality]}
          </p>
          {a.isOverbooking && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
              <Icon name="clock" className="h-3.5 w-3.5" />
              Cupo de sobrecupo — la espera puede ser mayor
            </p>
          )}
          <p className="mt-2 flex items-center gap-2 text-sm">
            <ProfessionalAvatar firstName={a.professional.firstName} lastName={a.professional.lastName} className="h-8 w-8 text-xs" />
            {a.professional.firstName} {a.professional.lastName}
            {a.professional.specialty ? <span className="text-brand-200"> · {a.professional.specialty}</span> : null}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-brand-100">
            <Icon name="location" className="h-4 w-4" />
            {a.modality === 'HOME_VISIT' && a.visitAddress ? `Visita a domicilio — ${a.visitAddress}` : `${a.location.name}${a.location.address ? ` — ${a.location.address}` : ''}`}
          </p>
          {a.modality === 'TELEMEDICINE' && a.videoCallUrl && (
            <a href={a.videoCallUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-700">
              <Icon name="video" className="h-4 w-4" />
              Entrar a la videollamada
            </a>
          )}
        </div>

        <ErrorText>{error}</ErrorText>

        {active && !past && (
          <div className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              {a.status === 'CONFIRMED' ? (
                <span className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-50 px-6 py-3 font-display text-sm font-semibold text-emerald-700">
                  <Icon name="check" className="h-4 w-4" />
                  Asistencia confirmada
                </span>
              ) : a.canConfirm ? (
                <button
                  type="button"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 font-display text-sm font-semibold text-white hover:bg-brand-700 disabled:bg-brand-300"
                >
                  <Icon name="scheduleCheck" className="h-4 w-4" />
                  {confirm.isPending ? 'Confirmando…' : 'Confirmar asistencia'}
                </button>
              ) : null}
              {a.canCancel && !askCancel && (
                <button
                  type="button"
                  onClick={() => setAskCancel(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-steel-300 bg-white px-6 py-3 font-display text-sm font-semibold text-steel-700 hover:border-red-300 hover:text-red-700"
                >
                  <Icon name="close" className="h-4 w-4" />
                  Anular hora
                </button>
              )}
            </div>

            {askCancel && a.canCancel && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="font-display font-semibold text-red-800">¿Anular esta hora?</p>
                <p className="mt-1 text-sm text-red-700">El cupo queda libre para otra persona. Puedes reservar de nuevo cuando quieras.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => cancel.mutate()}
                    disabled={cancel.isPending}
                    className="rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
                  >
                    {cancel.isPending ? 'Anulando…' : 'Sí, anular'}
                  </button>
                  <button type="button" onClick={() => setAskCancel(false)} className="rounded-full px-5 py-2 text-sm font-medium text-steel-700 hover:bg-white">
                    Mantener mi hora
                  </button>
                </div>
              </div>
            )}

            {a.lateCancellation && (
              <p className="flex items-start gap-2 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
                <Icon name="warning" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Faltan menos de {a.cancellationWindowHours} horas: para anular o cambiar esta hora llámanos al{' '}
                  <a href={`tel:${CLINIC.phone}`} className="font-semibold underline">{CLINIC.phone}</a> o escríbenos por{' '}
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline">WhatsApp</a>.
                </span>
              </p>
            )}
          </div>
        )}

        {a.status === 'CANCELLED' && (
          <div className="rounded-2xl border border-steel-100 bg-white p-4 text-sm text-steel-700">
            Esta hora fue anulada.{' '}
            <Link to="/agendar" className="font-medium text-brand-700 hover:underline">Reserva una nueva hora</Link> cuando quieras.
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          <Link to="/agendar" className="font-medium text-brand-700 hover:underline">Reservar otra hora</Link>
          <Link to="/" className="font-medium text-steel-600 hover:underline">Volver al sitio</Link>
        </div>
      </div>
    </BookingShell>
  )
}
